/**
 * Ordered bookmarks (Matthew, 2026-07-22).
 *
 * Apryse's user bookmarks are a page-keyed dictionary ({pageIndex: title})
 * and its stock Bookmarks panel always sorts by page number — there is no
 * setting to change that and no way to rearrange. The firm organizes
 * bookmarks by the date of the underlying record, so we keep Apryse's
 * dictionary (it drives the on-page bookmark icons and page navigation)
 * and carry the display order OUT OF BAND under a reserved "__order__" key
 * in the same persisted blob:
 *
 *   { "0": "2026 01 05 record", "7": "1993 04 05 Record", "__order__": [7, 0] }
 *
 * Old payloads without __order__ fall back to page order, so nothing needs
 * migrating. The stock panel is replaced by a custom left-panel tab
 * (renderable DOM inside the viewer iframe) with drag-to-reorder, rename,
 * delete, and "bookmark this page".
 */

export const ORDER_KEY = "__order__";

// docId -> array of page indices (display order)
const orderStore = new Map();
// instance -> panel controller
const controllers = new WeakMap();

const toPageIndex = (value) => {
  const n = Number.parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
};

/** Map of {pageIndex: title} without the reserved key. */
export const stripOrderKey = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  if (!(ORDER_KEY in payload)) return payload;
  const { [ORDER_KEY]: _omit, ...map } = payload;
  return map;
};

/** Split a stored payload into {map, order}. order is null when absent. */
export const splitOrderedPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { map: payload || null, order: null };
  }
  const map = stripOrderKey(payload);
  const rawOrder = payload[ORDER_KEY];
  if (!Array.isArray(rawOrder)) return { map, order: null };
  const seen = new Set();
  const order = [];
  for (const entry of rawOrder) {
    const page = toPageIndex(entry);
    if (page === null || seen.has(page)) continue;
    seen.add(page);
    order.push(page);
  }
  return { map, order };
};

/**
 * Reconcile a saved/edited page map with a previous display order:
 * keep the previous relative order for pages that still exist, append
 * new pages (ascending) at the end.
 */
export const mergeOrder = (map, previousOrder) => {
  const pages = Object.keys(map || {})
    .map(toPageIndex)
    .filter((n) => n !== null)
    .sort((a, b) => a - b);
  const pageSet = new Set(pages);
  const kept = (previousOrder || []).filter((p) => pageSet.has(p));
  const keptSet = new Set(kept);
  return [...kept, ...pages.filter((p) => !keptSet.has(p))];
};

/** Attach the order to a page map for persistence. */
export const withOrderPayload = (map, order) => {
  const clean = stripOrderKey(map) || {};
  const merged = mergeOrder(clean, order);
  if (Object.keys(clean).length === 0) return clean;
  return { ...clean, [ORDER_KEY]: merged };
};

export const getOrder = (docId) => orderStore.get(String(docId)) || null;

export const setOrder = (docId, order) => {
  orderStore.set(String(docId), Array.isArray(order) ? [...order] : []);
};

/** Build the persistable payload for a doc from a fresh viewer export. */
export const buildPersistPayload = (docId, exportedMap) => {
  const map = stripOrderKey(exportedMap) || {};
  const order = mergeOrder(map, getOrder(docId));
  setOrder(docId, order);
  return withOrderPayload(map, order);
};

// ── Custom panel (vanilla DOM — it renders inside the viewer iframe) ──────

const PANEL_TAB = "orderedBookmarksTab";
export const PANEL_ELEMENT = "orderedBookmarksPanel";

const BOOKMARK_TAB_ICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">' +
      '<path d="M6 3.5h12a1 1 0 0 1 1 1V21l-7-4.2L5 21V4.5a1 1 0 0 1 1-1z" ' +
      'stroke="currentColor" stroke-width="1.6" fill="none"/></svg>'
  );

const exportMap = (instance) => {
  try {
    if (instance.UI?.exportBookmarks) return stripOrderKey(instance.UI.exportBookmarks()) || {};
    if (typeof instance.exportBookmarks === "function") {
      return stripOrderKey(instance.exportBookmarks()) || {};
    }
  } catch (_) {}
  return {};
};

const importMap = (instance, map) => {
  try {
    if (instance.UI?.importBookmarks) instance.UI.importBookmarks(map);
    else if (typeof instance.importBookmarks === "function") instance.importBookmarks(map);
  } catch (_) {}
};

/**
 * Install the custom panel on a viewer instance (idempotent) and bind it to
 * a document. `requestPersist` is the same debounced save path used by
 * userBookmarksChanged, so reorders persist exactly like adds/deletes.
 *
 * The main viewer runs WebViewer 11's Modular UI (v11 default; the bundled
 * default left tabPanel filters out disabled panels, so disabling
 * "bookmarksPanel" removes the stock Bookmarks tab). Our panel registers
 * via UI.addPanel and opens from a header toggle button. The legacy-UI
 * setCustomPanel path is kept as a fallback for viewers created with
 * ui: 'legacy'.
 */
export const bindOrderedBookmarksPanel = (instance, docId, requestPersist) => {
  if (!instance?.UI) {
    console.warn("[ORDERED-BM] bind skipped: no instance.UI");
    return;
  }

  let controller = controllers.get(instance);
  if (!controller) {
    controller = { docId: null, requestPersist: null, container: null };
    controllers.set(instance, controller);

    // Diagnostics are deliberately loud (Matthew, 2026-07-22: panel didn't
    // appear on devdms and every failure here was a silent catch).
    const steps = [];

    const renderRoot = () => {
      const doc = instance.UI.iframeWindow?.document || document;
      const container = doc.createElement("div");
      container.className = "ordered-bm-panel";
      controller.container = container;
      renderPanel(instance, controller);
      return container;
    };

    let installed = false;
    let reachable = false; // user has a way to OPEN our panel
    if (typeof instance.UI.addPanel === "function") {
      try {
        instance.UI.addPanel({
          dataElement: PANEL_ELEMENT,
          location: "left",
          icon: BOOKMARK_TAB_ICON,
          render: renderRoot,
        });
        installed = true;
        steps.push("addPanel:ok");
      } catch (err) {
        steps.push(`addPanel:ERR ${err?.message}`);
      }
      // Header toggle so the panel is reachable like the stock one was.
      try {
        const ToggleElementButton = instance.UI.Components?.ToggleElementButton;
        const header = instance.UI.getModularHeader?.("default-top-header");
        if (installed && ToggleElementButton && header) {
          const alreadyThere = (header.getItems() || []).some(
            (item) => item?.dataElement === "orderedBookmarksToggle"
          );
          if (!alreadyThere) {
            const toggle = new ToggleElementButton({
              dataElement: "orderedBookmarksToggle",
              toggleElement: PANEL_ELEMENT,
              title: "Bookmarks",
              img: BOOKMARK_TAB_ICON,
            });
            header.setItems([...header.getItems(), toggle]);
          }
          reachable = true;
          steps.push(alreadyThere ? "toggle:already-present" : "toggle:ok");
        } else {
          steps.push(
            `toggle:skipped installed=${installed} btn=${!!ToggleElementButton} header=${!!header}`
          );
        }
      } catch (err) {
        steps.push(`toggle:ERR ${err?.message}`);
      }
    } else {
      steps.push("addPanel:missing");
    }
    if (!installed && typeof instance.UI.setCustomPanel === "function") {
      try {
        instance.UI.setCustomPanel({
          tab: {
            dataElement: PANEL_TAB,
            title: "Bookmarks",
            img: BOOKMARK_TAB_ICON,
          },
          panel: { dataElement: PANEL_ELEMENT, render: renderRoot },
        });
        installed = true;
        reachable = true; // legacy tab strip renders our tab directly
        steps.push("setCustomPanel:ok");
      } catch (err) {
        steps.push(`setCustomPanel:ERR ${err?.message}`);
      }
    }

    if (installed && reachable) {
      // The stock panel can't represent order — hide it so there is one
      // Bookmarks surface, ours. Only once ours is actually reachable;
      // otherwise users would be left with no bookmarks UI at all.
      controller.hideStock = true;
      try {
        instance.UI.disableElements(["bookmarksPanel", "bookmarksPanelButton"]);
        steps.push("disableStock:ok");
      } catch (err) {
        steps.push(`disableStock:ERR ${err?.message}`);
      }
    } else {
      steps.push("disableStock:skipped (panel not reachable)");
    }

    const summary = `[ORDERED-BM] install: ${steps.join(" | ")}`;
    if (steps.some((s) => s.includes("ERR") || s.includes("missing"))) {
      console.error(summary);
      try {
        // Surface install failures in Sentry so we can see them from
        // staging sessions without asking users for console output.
        const Sentry = require("@sentry/react");
        Sentry.captureMessage(summary, "warning");
      } catch (_) {}
    } else {
      console.log(summary);
    }
  }

  controller.docId = String(docId);
  controller.requestPersist = requestPersist || null;
  renderPanel(instance, controller);
  return () => {
    if (controllers.get(instance) === controller) {
      controller.requestPersist = null;
    }
  };
};

/** Re-render the panel after external bookmark changes (import, icon adds). */
export const refreshOrderedBookmarksPanel = (instance) => {
  const controller = controllers.get(instance);
  if (controller) renderPanel(instance, controller);
};

/**
 * Re-hide the stock bookmarks panel. configurePermissions calls
 * UI.enableAllElements() for editable PDFs, which clears the disabled state
 * and resurrects the stock tab next to ours (Matthew's devdms repro,
 * 2026-07-22) — call this after any permission pass.
 */
export const suppressStockBookmarksPanel = (instance) => {
  const controller = controllers.get(instance);
  if (!controller || !controller.hideStock) return;
  try {
    instance.UI.disableElements(["bookmarksPanel", "bookmarksPanelButton"]);
  } catch (_) {}
};

const renderPanel = (instance, controller) => {
  const { container, docId } = controller;
  if (!container || !docId) return;
  const doc = container.ownerDocument;
  const map = exportMap(instance);
  const order = mergeOrder(map, getOrder(docId));
  setOrder(docId, order);

  container.textContent = "";

  const persistSoon = () => {
    if (controller.requestPersist) controller.requestPersist();
  };

  const applyMapChange = (newMap) => {
    importMap(instance, newMap);
    // importBookmarks fires userBookmarksChanged -> debounced persist picks
    // up the new map and the order store. Re-render with fresh state.
    renderPanel(instance, controller);
  };

  // Toolbar
  const toolbar = doc.createElement("div");
  toolbar.className = "ordered-bm-toolbar";
  const addBtn = doc.createElement("button");
  addBtn.type = "button";
  addBtn.className = "ordered-bm-add";
  addBtn.textContent = "Bookmark this page";
  addBtn.addEventListener("click", () => {
    let pageIndex = 0;
    try {
      pageIndex = Math.max(0, (instance.Core?.documentViewer?.getCurrentPage() || 1) - 1);
    } catch (_) {}
    if (Object.prototype.hasOwnProperty.call(map, String(pageIndex))) return;
    const newMap = { ...map, [String(pageIndex)]: "Untitled" };
    setOrder(docId, [...order, pageIndex]);
    applyMapChange(newMap);
  });
  toolbar.appendChild(addBtn);
  container.appendChild(toolbar);

  const hint = doc.createElement("div");
  hint.className = "ordered-bm-hint";
  hint.textContent = order.length > 1 ? "Drag bookmarks to rearrange them." : "";
  container.appendChild(hint);

  const list = doc.createElement("div");
  list.className = "ordered-bm-list";
  container.appendChild(list);

  if (order.length === 0) {
    const empty = doc.createElement("div");
    empty.className = "ordered-bm-empty";
    empty.textContent = "No bookmarks yet.";
    list.appendChild(empty);
    return;
  }

  let dragFrom = null;

  order.forEach((pageIndex, position) => {
    const title = map[String(pageIndex)] ?? map[pageIndex] ?? "";
    const row = doc.createElement("div");
    row.className = "ordered-bm-row";
    row.draggable = true;
    row.dataset.position = String(position);

    row.addEventListener("dragstart", (e) => {
      dragFrom = position;
      row.classList.add("ordered-bm-dragging");
      try { e.dataTransfer.setData("text/plain", String(position)); } catch (_) {}
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    });
    row.addEventListener("dragend", () => {
      row.classList.remove("ordered-bm-dragging");
      dragFrom = null;
    });
    row.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      row.classList.add("ordered-bm-dragover");
    });
    row.addEventListener("dragleave", () => row.classList.remove("ordered-bm-dragover"));
    row.addEventListener("drop", (e) => {
      e.preventDefault();
      row.classList.remove("ordered-bm-dragover");
      let from = dragFrom;
      if (from === null) {
        const raw = (() => { try { return e.dataTransfer.getData("text/plain"); } catch (_) { return ""; } })();
        from = toPageIndex(raw);
      }
      if (from === null || from === position) return;
      const next = [...order];
      const [moved] = next.splice(from, 1);
      next.splice(position, 0, moved);
      setOrder(docId, next);
      renderPanel(instance, controller);
      persistSoon();
    });

    const grip = doc.createElement("span");
    grip.className = "ordered-bm-grip";
    grip.textContent = "⋮⋮";
    grip.setAttribute("aria-hidden", "true");
    row.appendChild(grip);

    const texts = doc.createElement("div");
    texts.className = "ordered-bm-texts";
    const pageEl = doc.createElement("div");
    pageEl.className = "ordered-bm-page";
    pageEl.textContent = `Page ${pageIndex + 1}`;
    const titleEl = doc.createElement("div");
    titleEl.className = "ordered-bm-title";
    titleEl.textContent = title;
    texts.appendChild(pageEl);
    texts.appendChild(titleEl);
    texts.addEventListener("click", () => {
      try { instance.Core?.documentViewer?.setCurrentPage(pageIndex + 1); } catch (_) {}
    });
    row.appendChild(texts);

    const startRename = () => {
      const input = doc.createElement("input");
      input.type = "text";
      input.className = "ordered-bm-rename-input";
      input.value = title;
      input.maxLength = 255;
      const commit = () => {
        const value = input.value.trim();
        if (!value || value === title) {
          renderPanel(instance, controller);
          return;
        }
        applyMapChange({ ...map, [String(pageIndex)]: value });
      };
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") renderPanel(instance, controller);
      });
      input.addEventListener("blur", commit);
      texts.replaceChild(input, titleEl);
      input.focus();
      input.select();
    };
    titleEl.addEventListener("dblclick", startRename);

    const renameBtn = doc.createElement("button");
    renameBtn.type = "button";
    renameBtn.className = "ordered-bm-action";
    renameBtn.title = "Rename bookmark";
    renameBtn.textContent = "✎";
    renameBtn.addEventListener("click", startRename);
    row.appendChild(renameBtn);

    const deleteBtn = doc.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "ordered-bm-action ordered-bm-delete";
    deleteBtn.title = "Delete bookmark";
    deleteBtn.textContent = "×";
    deleteBtn.addEventListener("click", () => {
      const newMap = { ...map };
      delete newMap[String(pageIndex)];
      delete newMap[pageIndex];
      setOrder(docId, order.filter((p) => p !== pageIndex));
      applyMapChange(newMap);
    });
    row.appendChild(deleteBtn);

    list.appendChild(row);
  });
};
